import React, { useEffect, useMemo, useState } from "react";
import { Row, Card, Typography, Form, Button, Divider, message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { propertyService } from "../../../../../configs/constants";
import { EditOutlined, CloseCircleOutlined, CheckCircleFilled } from "@ant-design/icons";
import dayjs from "dayjs";
import { getConfigData, getPropertyBeneficiaries } from "../../ducks/actions";
import { FormProvider, useForm } from "react-hook-form";
import axios from "axios";
import General from "../../AddProperty/components/AddInformation/General";
import ContractInfo from "../../AddProperty/components/AddInformation/ContractInfo";
import Address from "../../AddProperty/components/AddInformation/Address";
import BankAccountInfo from "../../AddProperty/components/AddInformation/BankAccountInfo";
import { getOrgByID } from "../../../Maintenance/Tickets/ducks/actions";
import { filterArray } from "../../../../../utils/filterdata";
import { usePopulatePropertyDetails } from "./hook/usePopulatePropertyDetails";
import { buildOrgParkingTypeOptions } from "../MarketingAttributes/buildOrgMarketingSelectFields";

const { Title } = Typography;

var _ = require("lodash");

export default ({ propertyDetails, setNewLessor, newLessor }) => {
  const form = useForm();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = form;

  const [noEdit, setNoEdit] = useState(true);
  const [propertyLessor, setPropertyLessor] = useState<{ label: string; value: string }[]>([]);

  const [lessorInitial, setLessorInitial] = useState<{ label: string; value: string } | null>(null);
  const [benificiaryInitial, setBenificiaryInitail] = useState<{ value: string; label: string } | null>(null);
  const [ownerInitial, setOwnerInitail] = useState<{
    value: string;
    label: string;
    ownerId?: string;
    ownerNumber?: string;
    ownerNationality?: string;
  } | null>(null);
  const [bankNameInitial, setBankNameInitial] = useState<{ label: string; value: string } | null>(null);
  const [bankList, setBankList] = useState<{ label: string; value: string }[]>([]);
  const apiUnitTypeData = useSelector((state: any) => state.addInfoReducer.getUnitType);
  const organization = useSelector((state: any) => state.tickets.orgData);
  const configData = useSelector((state: any) => state.dashboard.configData);
  const apiUnitSubTypeData = useSelector((state: any) => state.addInfoReducer.getUnitSubType);
  const dispatch: any = useDispatch();
  const configKeys = ["locationlist", "emiratesList", "nationalityList", "bankList"];

  const parkingPlaceOptions = useMemo(
    () => buildOrgParkingTypeOptions(organization?.marketingDefaultOrgConfig?.property),
    [organization?.marketingDefaultOrgConfig?.property],
  );

  const getPropertyBeneficiariesData = async () => {
    try {
      let data = await getPropertyBeneficiaries();
      const filtered = filterArray(data?.lessor, (item) => item?.lessorName !== "");
      data?.lessor?.length > 0 && setPropertyLessor(filtered);
      let lessorData =
        propertyDetails?.lessorId && data?.lessor?.find((lessor) => lessor?.lessorId === propertyDetails?.lessorId);

      lessorData &&
        setLessorInitial({
          label: lessorData?.lessorName,
          value: lessorData?.lessorId,
        });

      propertyDetails?.beneficiaryData &&
        setBenificiaryInitail({
          value: propertyDetails?.beneficiaryData?.beneficiaryId,
          label: propertyDetails?.beneficiaryData?.beneficiaryName,
        });
    } catch (error) {
      console.log("error getting beneficiary", error);
    }
  };

  useEffect(() => {
    const isOrgEmpty = !organization || (typeof organization === "object" && Object.keys(organization).length === 0);

    if (isOrgEmpty) {
      const orgData = localStorage?.getItem("login-response");
      const orgId = orgData ? JSON.parse(orgData) : null;
      if (orgId) dispatch(getOrgByID(orgId?.orgId));
    }

    dispatch(getConfigData(configKeys.join(",")));
  }, []);

  useEffect(() => {
    if (configData && configData?.bankList && Object.keys(configData?.bankList)?.length > 0) {
      const bankList = configData?.bankList || {};
      const formattedBankList = Object?.entries(bankList as Record<string, string>).map(([key, label]) => ({
        label,
        value: label.toLowerCase().replace(/\s+/g, "_"),
      }));
      setBankList(formattedBankList);
    }
  }, [configData?.bankList]);

  useEffect(() => {
    if (apiUnitTypeData.length) {
      let unitlist = apiUnitTypeData.find((item) => item.id === propertyDetails.unit_type_id);
      if (unitlist) {
        setValue("unit_type_id", { value: unitlist.id, label: unitlist.name });
      }
    }
  }, [apiUnitTypeData]);

  useEffect(() => {
    if (apiUnitSubTypeData.length) {
      let sublist: any = {};
      sublist = apiUnitSubTypeData.find((item) => item.id === propertyDetails.unit_subtype_id);
      if (sublist) {
        setValue("unit_subtype_id", { value: sublist.id, label: sublist.name });
      } else {
        setValue("unit_subtype_id", "");
      }
    }
  }, [apiUnitSubTypeData]);

  useEffect(() => {
    if (!noEdit) {
      let featureSelected = [];
      let amenitySelected = [];
      setValue("property_features", featureSelected);
      setValue("property_amenities", amenitySelected);
    }
  }, [noEdit]);

  useEffect(() => {
    getPropertyBeneficiariesData();
  }, [propertyDetails]);

  // mobile number have 971 code remove it

  function removeCountryCode(phoneNumber) {
    if (phoneNumber.startsWith("971-")) {
      return phoneNumber.slice(4); // Remove the first 4 characters (971-)
    }
    return phoneNumber;
  }

  usePopulatePropertyDetails({
    propertyDetails,
    bankList,
    setValue,
    setOwnerInitail, // ✅ correct spelling
    setBankNameInitial,
    removeCountryCode,
  });

  const onSubmit = async (val) => {
    const date =
      val?.lessorLicenseExpiry &&
      dayjs(
        new Date(
          val?.lessorLicenseExpiry.$y,
          val?.lessorLicenseExpiry.$M,
          val?.lessorLicenseExpiry.$D,
          val?.lessorLicenseExpiry.$H,
          val?.lessorLicenseExpiry.$m,
          val?.lessorLicenseExpiry.$s,
          val?.lessorLicenseExpiry.$ms,
        ),
      );

    const lessorLicenseExpiry = date && date.format("YYYY-MM-DD");

    const numberOfFloors =
      val?.numberOfFloors === "" || val?.numberOfFloors == null ? null : Number(val.numberOfFloors);
    const parkingSelect = val?.parkingTypes;
    const parkingTypes =
      parkingSelect?.value != null && parkingSelect?.value !== ""
        ? { label: String(parkingSelect.label ?? ""), value: parkingSelect.value }
        : null;

    const postJson = {
      propertyAdmin: val?.propertyAdmin?.value,
      erpLaName: val?.propertyAdmin?.label,
      erpLaEmail: val?.propertyAdmin?.email,

      address: {
        addressLine1: val.addressLine1,
        addressLine2: val.addressLine2,
        emirates: val.emirates.value,
        country: val.country.value,
        community: val.community.value,
        latitude: val.latitude,
        longitude: val.longitude,
      },
      // after story 9783 adding these fields
      // ownerName: val?.ownerName,
      // ownerNationality: val?.ownerNationality?.value,
      // ownerNumber:val?.ownerNumber,
      alternatePropertyName: val?.alternatePropertyName,
      landNnumber: val?.landNnumber,
      DMNumber: val?.DMNumber,
      makaniNumber: val?.makaniNumber,
      beneficiaryId: benificiaryInitial?.value,
      // beneficiary: benificiaryInitial?.value
      //   ? {
      //       beneficiaryId: benificiaryInitial?.value,
      //       beneficiaryName: benificiaryInitial?.label,
      //       beneficiaryAccountNumber: val?.accountNo,
      //       beneficiaryBank: bankNameInitial?.label,
      //       beneficiaryIBAN: val?.iban,
      //     }
      //   : {},
      owner: {
        name: ownerInitial?.value,
        nationality: val?.ownerNationality?.value,
        number: val?.ownerNumber,
        ownerId: ownerInitial?.ownerId,
      },
      lessor: {
        lessorId: lessorInitial?.value,
        lessorNameAr: val?.lessorNameAr,
        lessorLicenseNo: val?.lessorLicenseNo,
        lessorLicenseExpiry: lessorLicenseExpiry,
        lessorTel: val?.lessorPhone,
        lessorEmail: val?.lessorEmail,
        lessorAddress: val?.lessorAddress,
        lessorLetterPOBox: val?.lessorPOBox,
        lessorLetterFax: val?.lessorFax,
        beneficiary: {
          ...val?.lessor?.beneficiary,
          bankName: val?.lessor?.beneficiary?.bankName?.label,
        },
      },
      marketingData: {
        numberOfFloors: numberOfFloors != null && !Number.isNaN(numberOfFloors) ? numberOfFloors : null,
        parkingTypes,
      },
    };

    try {
      await axios.patch(`${propertyService}/property/${propertyDetails.propertyId}`, postJson);
      setNewLessor(!newLessor);
      message.success("Property Details Updated");
      setNoEdit(true);
      await getPropertyBeneficiariesData();
    } catch (e) {
      const { response } = e as { response: { data: { error: string } } } ;
      message.error(response.data.error);
      setNoEdit(true);
    }
  };

  return (
    <FormProvider {...form}>
      <Form layout="vertical" name="bookingform" className="bf-main" onFinish={handleSubmit(onSubmit)}>
        <Card
          bordered={false}
          bodyStyle={{ padding: "16px 32px 0" }}
          headStyle={{ padding: "16px 32px 0", borderBottom: 0 }}
          title={
            <Title level={5} className="ag-primary mb-0">
              General Information
            </Title>
          }
          extra={[
            <Button
              key={1}
              type="link"
              className={`p-0 no-border no-shadow ${noEdit && "d-none"}`}
              icon={<CloseCircleOutlined />}
              onClick={() => setNoEdit(true)}
              htmlType="button"
            >
              Cancel
            </Button>,
            <Button
              key={2}
              type="link"
              className={`p-0 no-border no-shadow ${noEdit && "d-none"}`}
              icon={<CheckCircleFilled />}
              htmlType="submit"
            >
              Save
            </Button>,
            <Button
              key={3}
              type="link"
              className={`p-0 no-border no-shadow ${!noEdit && "d-none"}`}
              icon={<EditOutlined />}
              onClick={() => setNoEdit(false)}
              htmlType="button"
            >
              Edit
            </Button>,
          ]}
        >
          <General control={control} errors={errors} setValue={setValue} noEdit={noEdit} />
        </Card>

        <Divider className="m-0" />

        <Card
          bordered={false}
          bodyStyle={{ padding: "16px 32px 0" }}
          headStyle={{ padding: "16px 32px 0", borderBottom: 0 }}
          title={
            <Title level={5} className="ag-primary mb-0">
              Contract Information
            </Title>
          }
        >
          <ContractInfo
            control={control}
            errors={errors}
            setLessorInitial={setLessorInitial}
            setBankNameInitial={setBankNameInitial}
            propertyDetails={propertyDetails}
            noEdit={noEdit}
            setValue={setValue}
            setNewLessor={setNewLessor}
            newLessor={newLessor}
            propertyLessor={propertyLessor}
            lessorLicenseExpiry={propertyDetails?.lessorData?.lessorLicenseExpiry}
            lessorInitial={lessorInitial}
            bankNameInitial={bankNameInitial}
            bankList={bankList}
            lessorNameArabic={propertyDetails?.lessorData?.lessorNameAr}
          />
        </Card>

        <Divider className="m-0" />

        <Card
          bordered={false}
          bodyStyle={{ padding: "16px 32px 0" }}
          headStyle={{ padding: "16px 32px 0", borderBottom: 0 }}
          title={
            <Title level={5} className="ag-primary mb-0">
              Property Information
            </Title>
          }
        >
          <Row gutter={24}>
            <Address
              control={control}
              errors={errors}
              setValue={setValue}
              noEdit={noEdit}
              parkingPlaceOptions={parkingPlaceOptions}
            />
          </Row>
        </Card>

        <Divider className="m-0" />

        <Card
          bordered={false}
          bodyStyle={{ padding: "16px 32px 0" }}
          headStyle={{ padding: "16px 32px 0", borderBottom: 0 }}
          title={
            <Title level={5} className="ag-primary mb-0">
              Owner and Bank Account Information
            </Title>
          }
        >
          <BankAccountInfo
            control={control}
            ownerInitial={ownerInitial}
            setBenificiaryInitail={setBenificiaryInitail}
            benificiaryInitial={benificiaryInitial}
            setBankNameInitial={setBankNameInitial}
            bankNameInitial={bankNameInitial}
            propertyId={propertyDetails.propertyId}
            errors={errors}
            setOwnerInitail={setOwnerInitail}
            setValue={setValue}
            setNewLessor={setNewLessor}
            noEdit={noEdit}
            newLessor={newLessor}
            bankList={bankList}
          />
        </Card>
      </Form>
    </FormProvider>
  );
};
